import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';
import { openSupportChat } from '@/utils/supportChat';

const CLAIM_STEPS = [
  {
    id: 'chat',
    step: '01',
    title: 'Open the support chat',
    text: 'Click the chat bubble in the bottom-right corner, or use the button below. It is staffed by a real person.',
  },
  {
    id: 'details',
    step: '02',
    title: 'Tell us three things',
    text: 'Your broker, your account balance and where you run MetaTrader 5 (Windows PC, Mac, or a VPS). That is all we need.',
  },
  {
    id: 'receive',
    step: '03',
    title: 'Receive your files',
    text: 'We reply with GoldWarden.ex5, a preset matched to your balance, and the setup guide — all in the same conversation.',
  },
  {
    id: 'install',
    step: '04',
    title: 'Install and go live',
    text: 'Follow the six-step guide, or ask us to walk you through it live in the chat. Most people are trading within five minutes.',
  },
];

export default function ClaimSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="How to claim"
              title="Request it free through the support chat"
              subtitle="No checkout, no payment details, no account sign-up. Just a quick conversation and the files are yours."
            />
          </Reveal>

          <div className="mt-10 md:mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <div className="flex flex-col gap-4">
              {CLAIM_STEPS.map((item, index) => (
                <Reveal key={item.id} delay={index * 70}>
                  <div className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-primary-950 text-accent-400 font-num text-sm font-bold">
                      {item.step}
                    </span>
                    <div>
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-xs md:text-sm leading-relaxed text-foreground-600">{item.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="lg:sticky lg:top-28 self-start">
              <Reveal delay={140}>
              <div className="rounded-lg bg-primary-950 p-6 md:p-8 flex flex-col justify-center">
                <span className="inline-flex w-fit items-center gap-2 rounded-full bg-background-50/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-accent-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" aria-hidden="true"></span>
                  Support online
                </span>
                <h3 className="mt-5 font-heading text-xl md:text-2xl font-bold text-background-50 text-balance">
                  Ready to get GoldWarden EA for free?
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-background-200/85">
                  Start the chat and tell us your broker and balance. We will send the EA, the preset and
                  the guide in the same conversation.
                </p>

                <ul className="mt-6 flex flex-col gap-3">
                  {[
                    'No payment details requested',
                    'Honest answers, no pushy upsell',
                    'Live help during your first install',
                  ].map((line) => (
                    <li key={line} className="flex items-center gap-2.5 text-xs md:text-sm text-background-200/90">
                      <i className="ri-check-double-line text-accent-400" aria-hidden="true"></i>
                      {line}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={openSupportChat}
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-accent-500 hover:bg-accent-400 px-7 py-3.5 text-sm md:text-base font-semibold text-primary-950 transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-customer-service-2-line text-lg" aria-hidden="true"></i>
                  Start the chat
                </button>
              </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}